import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Rating,
  Snackbar,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { fi } from 'date-fns/locale';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';

import { can } from '../../utils/access';
function Section({ title, children }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.5px' }}>
        {title}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

export default function FeedbackDetailDialog({ open, onClose, feedbackId, onFollowupActioned }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;
  const canAction = can(currentOrganization, 'feedback.action');

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actioning, setActioning] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchDetail = useCallback(async () => {
    if (!feedbackId) return;
    setLoading(true);
    try {
      const response = await axios.get(`/api/feedback-responses/${feedbackId}/`, {
        params: { organization: orgId },
      });
      setDetail(response.data);
    } catch (err) {
      console.error('Failed to load feedback detail:', err);
    } finally {
      setLoading(false);
    }
  }, [feedbackId, orgId]);

  useEffect(() => {
    if (open && feedbackId) {
      fetchDetail();
    }
    if (!open) {
      setDetail(null);
    }
  }, [open, feedbackId, fetchDetail]);

  const handleMarkActioned = async () => {
    if (actioning) return;
    setActioning(true);
    try {
      await axios.post(
        `/api/feedback-responses/${feedbackId}/mark_followup_actioned/`,
        {},
        { params: { organization: orgId } }
      );
      await fetchDetail();
      onFollowupActioned?.();
      setSnackbar({ open: true, message: t('customer_feedback.followup_actioned_success'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || t('Error'), severity: 'error' });
    } finally {
      setActioning(false);
    }
  };

  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'd.M.yyyy HH:mm', { locale: fi });
    } catch {
      return dateStr;
    }
  };

  const channelLabels = {
    email: t('feedback_settings.channel_email'),
    sms: t('feedback_settings.channel_sms'),
    both: t('feedback_settings.channel_both'),
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        {loading || !detail ? (
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </DialogContent>
        ) : (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
              <Rating value={detail.response?.rating || 0} readOnly size="large" />
              <Typography variant="body2" color="text.secondary">
                {formatDate(detail.response?.submitted_at)}
              </Typography>
            </DialogTitle>

            <DialogContent dividers>
              {/* Customer */}
              <Section title={t('customer_feedback.section_customer')}>
                {detail.customer ? (
                  <>
                    <Link
                      component="button"
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'left' }}
                      onClick={() => handleNavigate('/customers')}
                    >
                      {detail.customer.display_name}
                    </Link>
                    {detail.customer.email && (
                      <Typography variant="body2" color="text.secondary">
                        <Link href={`mailto:${detail.customer.email}`} color="inherit">
                          {detail.customer.email}
                        </Link>
                      </Typography>
                    )}
                    {detail.customer.phone && (
                      <Typography variant="body2" color="text.secondary">
                        <Link href={`tel:${detail.customer.phone}`} color="inherit">
                          {detail.customer.phone}
                        </Link>
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">-</Typography>
                )}
              </Section>

              {/* Vehicle */}
              <Section title={t('customer_feedback.section_vehicle')}>
                {detail.vehicle ? (
                  <Typography variant="body1">
                    <strong>{detail.vehicle.registration_number}</strong>
                    {' '}
                    {detail.vehicle.make} {detail.vehicle.model}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">-</Typography>
                )}
              </Section>

              {/* Work Order */}
              <Section title={t('customer_feedback.section_work_order')}>
                {detail.maintenance ? (
                  <Box>
                    <Link
                      component="button"
                      variant="body1"
                      sx={{ fontWeight: 600, textAlign: 'left' }}
                      onClick={() => handleNavigate(`/maintenances/${detail.maintenance.id}`)}
                    >
                      #{detail.maintenance.work_number}
                    </Link>
                    {detail.maintenance.completed_at && (
                      <Typography variant="body2" color="text.secondary">
                        {t('customer_feedback.completed_at')}: {formatDate(detail.maintenance.completed_at)}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">-</Typography>
                )}
              </Section>

              <Divider sx={{ my: 1.5 }} />

              {/* Comment */}
              <Section title={t('customer_feedback.section_feedback')}>
                {detail.response?.comment ? (
                  <Box sx={{
                    bgcolor: 'grey.50', borderRadius: 1, p: 2,
                    borderLeft: '3px solid', borderColor: 'primary.light',
                  }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {detail.response.comment}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('customer_feedback.no_comment')}
                  </Typography>
                )}
              </Section>

              {/* Delivery */}
              <Section title={t('customer_feedback.section_delivery')}>
                <Typography variant="body2">
                  {t('customer_feedback.channel')}: {channelLabels[detail.request?.channel] || detail.request?.channel || '-'}
                </Typography>
                <Typography variant="body2">
                  {t('customer_feedback.sent_at')}: {formatDate(detail.request?.sent_at)}
                </Typography>
              </Section>

              {/* Google */}
              <Section title={t('customer_feedback.section_google')}>
                <Typography variant="body2">
                  {t('customer_feedback.google_shown')}: {detail.response?.google_cta_shown ? '✓' : '-'}
                </Typography>
                <Typography variant="body2">
                  {t('customer_feedback.google_clicked')}: {detail.response?.google_cta_clicked ? '✓' : '-'}
                </Typography>
              </Section>

              {/* Followup */}
              {detail.response?.low_rating_followup_requested && (
                <Section title={t('customer_feedback.section_followup')}>
                  {detail.response.low_rating_followup_actioned ? (
                    <Box>
                      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                        {t('customer_feedback.followup_actioned')}
                      </Typography>
                      {detail.response.low_rating_followup_actioned_at && (
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(detail.response.low_rating_followup_actioned_at)}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600, mb: 1 }}>
                        {t('customer_feedback.followup_requested')}
                      </Typography>
                      {canAction && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleMarkActioned}
                          disabled={actioning}
                          sx={{ textTransform: 'none' }}
                        >
                          {actioning ? <CircularProgress size={20} /> : t('customer_feedback.mark_followup_actioned')}
                        </Button>
                      )}
                    </Box>
                  )}
                </Section>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose}>{t('Close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
